import type { UseInfiniteQueryResult } from "@tanstack/react-query";
import { SCREENER_COLUMNS, type ScreenerColumn } from "epsm-api-types/models";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useFilterData } from "@/context/FilterDataContext";
import {
	useWidgetView,
	type ViewState,
	type WidgetViewContextProps,
} from "@/context/WidgetViewContext";
import { escapeRegex } from "@/lib/utils";
import sprites from "/src/shared/assets/sprite.svg";

export const StringFilters = ({
	refetch,
}: {
	refetch: UseInfiniteQueryResult["refetch"];
}) => {
	const { selectedColumn } = useWidgetView() as {
		selectedColumn: ViewState<ScreenerColumn>;
	} & WidgetViewContextProps;
	const { updateResults, field, setField } = useFilterData();

	const handleUpdateResults = () => {
		const newResult: Array<{
			name: string;
			condition: "regex";
			value: unknown;
			options: string;
		}> = [];

		newResult.push({
			name: selectedColumn.Screener.name,
			condition: "regex",
			value: escapeRegex(field),
			options: "i",
		});

		updateResults(newResult, refetch);
	};

	const column = SCREENER_COLUMNS.find(
		(column) => column.name === selectedColumn.Screener.name,
	);

	const columnAbbr =
		column?.titleAbbreviation ?? column?.titleShort ?? column?.title;
	const columnTitle =
		column?.title === columnAbbr
			? column?.title
			: `${column?.title} (${columnAbbr})`;

	return (
		<div className="flex flex-col relative max-w-[360px] w-full pt-0.5">
			<h3 className="text-[var(--light-gray)]">{columnTitle}</h3>

			<div className="flex flex-col relative max-w-[360px] w-full mt-5 mb-3 pt-0.5">
				<svg
					aria-label="search"
					role="img"
					fill="none"
					stroke="var(--light-gray)"
					className="w-2.5 h-2.5 absolute top-[6px] ml-1.5"
				>
					<use xlinkHref={`${sprites}#search`} />
				</svg>

				<Input
					className="bg-background pl-5"
					type="text"
					placeholder="Search field..."
					value={field}
					onChange={(event) => setField(event.target.value)}
				/>
			</div>

			<Button
				className="self-end h-[18px]"
				variant="outline"
				disabled={!field}
				onClick={handleUpdateResults}
			>
				Add
			</Button>
		</div>
	);
};
